import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import type { Identity } from '@/types/Common'

// After this long without a touch/key the app locks itself (logout + back to Domov).
export const IDLE_LOCK_MS = 5 * 60 * 1000

const LAST_ACTIVITY_KEY = 'lastActivity'

interface TokenClaims extends Identity {
  must_change: boolean
  exp?: number
}

// Decodes the JWT payload client-side — the API signs it, we only ever read the
// name/is_admin/must_change claims it already carries, never trust it for anything
// security sensitive (every real check happens server-side).
const decodeClaims = (jwt: string): TokenClaims | undefined => {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return undefined

    // atob yields Latin-1; the payload is UTF-8 (names with č, š, ž), so decode the bytes properly.
    const binary = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0))))

    return { name: claims.name, is_admin: !!claims.is_admin, must_change: !!claims.must_change, exp: claims.exp }
  } catch {
    return undefined
  }
}

// A token left in the browser is only trusted while it is unexpired and the last
// activity was within the idle window — otherwise the app starts locked.
const loadStoredJWT = (): string => {
  const jwt = localStorage.getItem('JWT') || ''
  if (!jwt) return ''

  const claims = decodeClaims(jwt)
  // A pending forced code change cannot be resumed after a reload (the current code is not kept).
  const forcedChange = !!claims?.must_change
  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0)
  const expired = !claims || (claims.exp !== undefined && claims.exp * 1000 < Date.now())
  const idle = Date.now() - lastActivity > IDLE_LOCK_MS

  if (expired || idle || forcedChange) {
    localStorage.removeItem('JWT')
    return ''
  }

  return jwt
}

export type OverlayRequest = 'admin' | 'change-pin'

export const useAppStore = defineStore('app', () => {
  const JWT = ref(loadStoredJWT())
  const locale = ref(localStorage.getItem('locale') || 'sl')
  const windowWidth = ref(window.innerWidth)
  const loaderActive = ref(0)
  const initialClaims = JWT.value ? decodeClaims(JWT.value) : undefined
  const identity = ref<Identity | undefined>(
    initialClaims ? { name: initialClaims.name, is_admin: initialClaims.is_admin } : undefined,
  )
  // True while the account still has its initial code: the whole app stays locked
  // behind the "Izberi svojo kodo" screen until /change-pin succeeds.
  const mustChangePin = ref(!!initialClaims?.must_change)

  // Lets a view deep in the router tree (e.g. Šifranti, the session card) ask the
  // always-mounted LockOverlay to reopen in admin / change-pin mode.
  const overlayRequest = ref<OverlayRequest | null>(null)
  const requestAdminLogin = () => (overlayRequest.value = 'admin')
  const requestChangePin = () => (overlayRequest.value = 'change-pin')
  const clearOverlayRequest = () => (overlayRequest.value = null)

  // Stores/components holding half-filled form state register a reset here; it runs on
  // every logout (manual lock, idle lock, 401) so the next person never sees a draft.
  const lockHandlers = new Set<() => void>()
  const registerLockHandler = (handler: () => void) => {
    lockHandlers.add(handler)
    return () => lockHandlers.delete(handler)
  }

  // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
  // never a second source of truth. Values: 'auto' | 'light' | 'dark'
  const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

  const getJWT = () => JWT.value
  const isLoggedIn = () => !!JWT.value
  const isLoaderActive = () => loaderActive.value > 0
  const isMobile = () => windowWidth.value < 1024
  // Fully usable session: signed in and no forced code change pending.
  const isUnlocked = () => !!JWT.value && !mustChangePin.value

  const touchActivity = () => localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))

  const setLogin = (jwt: string, mustChange = false) => {
    const claims = decodeClaims(jwt)
    localStorage.setItem('JWT', jwt)
    touchActivity()
    JWT.value = jwt
    identity.value = claims ? { name: claims.name, is_admin: claims.is_admin } : undefined
    mustChangePin.value = mustChange || !!claims?.must_change
  }

  const setLogout = () => {
    localStorage.removeItem('JWT')
    JWT.value = ''
    identity.value = undefined
    mustChangePin.value = false
    overlayRequest.value = null
    lockHandlers.forEach((handler) => handler())
  }

  const startLoading = () => loaderActive.value++
  const stopLoading = () => (loaderActive.value = Math.max(0, loaderActive.value - 1))

  const changeLocale = (newLocale: string) => {
    locale.value = newLocale
    localStorage.setItem('locale', newLocale)
    ;(i18n.global.locale as unknown as { value: string }).value = newLocale
  }

  return {
    JWT,
    locale,
    identity,
    loaderActive,
    colorMode,
    mustChangePin,
    overlayRequest,
    getJWT,
    isLoggedIn,
    isUnlocked,
    isLoaderActive,
    isMobile,
    setLogin,
    setLogout,
    touchActivity,
    startLoading,
    stopLoading,
    changeLocale,
    requestAdminLogin,
    requestChangePin,
    clearOverlayRequest,
    registerLockHandler,
  }
})
